import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ToastContainer } from "react-toastify";
import * as axios from "axios";
import {
  Alert,
  Badge,
  Breadcrumbs,
  Button,
  Checkbox,
  Code02,
  Copy01,
  CopyButton,
  Edit05,
  Eye,
  Modal,
  PageHeader,
  Plus,
  Radio,
  RadioGroup,
  Select,
  Tag,
  TextInput,
  Toggle,
} from "@opendate-app/setlist";

import { toastError, toastSuccess } from "../../../shared/toastHelper";
import buildSignupFormEmbed from "./buildSignupFormEmbed";
import signupFormBuilderStyles from "./signupFormBuilderStyles";
import { FIELD_ORDER, FIELD_TITLES, agreementChannel } from "./signupFormFields";
import BuilderSection from "./components/BuilderSection";
import ColorField from "./components/ColorField";
import ConsentItem from "./components/ConsentItem";
import CustomQuestionsEditor from "./components/CustomQuestionsEditor";
import EmailAlertsEditor from "./components/EmailAlertsEditor";
import FormPreview from "./components/FormPreview";
import FormStatusBadge from "./components/FormStatusBadge";
import { listHref } from "../shared/listUrlState";

const AGREEMENT_TEXT = {
  email: "I agree to receive marketing emails. Unsubscribe any time.",
  sms: "I agree to receive automated marketing text messages. Msg & data rates may apply. Reply STOP to opt out.",
};

const AGREEMENT_HINT = {
  email: "Fans who leave it unticked still submit the form — they just don't join the email list.",
  sms: "Fans who leave it unticked still submit the form — they just don't join the text list. Carriers require this box to start unticked.",
};

const isUntouchedAgreement = (consent, channel) =>
  String(consent.text || "").replace(/&amp;/g, "&") === AGREEMENT_TEXT[channel] && !consent.checked;

const CHANNEL_SECTIONS = [
  { key: "email", title: "Email marketing", desc: "Subscribes fans to your email list." },
  { key: "sms", title: "SMS marketing", desc: "Subscribes fans to text messages, and switches on the phone field." },
];

const SignupFormBuilderContainer = (props) => {
  const { csrfToken, team, venueOwnerships = [], teamMembers = [], signupForm, embedBaseUrl } = props;
  const isPersisted = !!signupForm.id;
  const cfg = signupForm.config || {};

  // Per-instance counter for client-only consent ids; a ref (not module scope) so it can't leak across SSR requests.
  const cidSeq = useRef(0);
  const nextCid = () => (cidSeq.current += 1);

  const [name, setName] = useState(signupForm.name || (isPersisted ? "" : "New form"));
  const [status, setStatus] = useState(signupForm.status || "draft");
  const [venueIds, setVenueIds] = useState(signupForm.venue_ownership_ids || []);
  const [fields, setFields] = useState(() => ({ ...cfg.fields }));
  const [channels, setChannels] = useState(() => ({
    email: isPersisted ? !!signupForm.subscribe_to_marketing_email : true,
    sms: !!signupForm.subscribe_to_marketing_sms,
  }));
  const [letChoose, setLetChoose] = useState(!!cfg.subscription_preferences?.choose_your_channel);
  const [consents, setConsents] = useState(() => (cfg.consents || []).map((c) => {
    const channel = agreementChannel(c);
    return { ...c, cid: nextCid(), ...(channel ? { channel } : {}) };
  }));
  const [questions, setQuestions] = useState(() => (Array.isArray(cfg.questions) ? cfg.questions : []));
  const [alertIds, setAlertIds] = useState(() => {
    const saved = Array.isArray(cfg.alerts?.team_membership_ids) ? cfg.alerts.team_membership_ids : [];
    return saved.filter((id) => teamMembers.some((m) => m.id === id));
  });
  const [tags, setTags] = useState(signupForm.default_tags || []);
  const [tagDraft, setTagDraft] = useState("");
  const [appearance, setAppearance] = useState(() => ({ ...cfg.appearance }));
  const [text, setText] = useState(() => ({ ...cfg.text }));
  const [success, setSuccess] = useState(() => ({
    mode: cfg.post_submit?.mode || "message",
    message: cfg.post_submit?.message || "Thanks for subscribing!",
    redirect: cfg.post_submit?.redirect_url || "",
  }));

  const [formId, setFormId] = useState(signupForm.id || null);
  const [savedUuid, setSavedUuid] = useState(signupForm.uuid || "");
  const [publicPath, setPublicPath] = useState(signupForm.public_path || "");
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const editorRefs = useRef({});
  const nameRef = useRef(null);

  const buildConfig = () => ({
    appearance,
    text,
    fields,
    subscription_preferences: { choose_your_channel: letChoose },
    consents: consents.map(({ cid, ...c }) => c).filter((c) => (c.text || "").trim()),
    questions,
    alerts: { team_membership_ids: alertIds },
    post_submit: { mode: success.mode, message: success.message, redirect_url: success.redirect },
  });

  const formForEmbed = useMemo(
    () => ({ uuid: savedUuid, public_path: publicPath, config: { text } }),
    [savedUuid, publicPath, text]
  );

  const hasVenue = venueIds.length > 0;
  const hiddenVenueIds = venueIds.filter((id) => !venueOwnerships.some((vo) => vo.id === id));
  const isLive = status === "active";

  const setField = (key, patch) => setFields((f) => ({ ...f, [key]: { ...f[key], ...patch } }));
  const setAppr = (patch) => setAppearance((a) => ({ ...a, ...patch }));

  const addConsent = () => setConsents((c) => [...c, { cid: nextCid(), text: "", required: false, checked: false }]);
  const updateConsent = (cid, patch) => setConsents((c) => c.map((x) => (x.cid === cid ? { ...x, ...patch } : x)));
  const removeConsent = (cid) => setConsents((c) => c.filter((x) => x.cid !== cid));

  const agreementsFor = (channel) => consents.filter((c) => agreementChannel(c) === channel);
  const standaloneConsents = consents.filter((c) => !agreementChannel(c));

  const addAgreement = (channel) =>
    setConsents((list) => (
      list.some((c) => agreementChannel(c) === channel)
        ? list
        : [...list, { cid: nextCid(), text: AGREEMENT_TEXT[channel], required: false, checked: false, channel }]
    ));

  const dropUntouchedAgreement = (list, channel) =>
    list.filter((c) => agreementChannel(c) !== channel || !isUntouchedAgreement(c, channel));

  const toggleChannel = (channel) => {
    const next = !channels[channel];
    setChannels((c) => ({ ...c, [channel]: next }));
    if (next && channel === "sms") setField("phone", { enabled: true });
    setConsents((list) => {
      if (!next) return dropUntouchedAgreement(list, channel);
      if (channel !== "sms") return list;
      if (list.some((c) => agreementChannel(c) === channel)) return list;
      return [...list, { cid: nextCid(), text: AGREEMENT_TEXT[channel], required: false, checked: false, channel }];
    });
  };

  const addTag = (raw) => {
    const t = raw.trim().toLowerCase();
    if (t && !tags.includes(t)) setTags((ts) => [...ts, t]);
    setTagDraft("");
  };

  const payload = () => ({
    signup_form: {
      name,
      subscribe_to_marketing_email: channels.email,
      subscribe_to_marketing_sms: channels.sms,
      default_tags: tags,
      venue_ownership_ids: venueIds,
      config: buildConfig(),
    },
  });

  const withCsrf = () => { axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken; };

  const persist = async () => {
    withCsrf();
    const res = formId
      ? await axios.patch(`/teams/${team.id}/signup_forms/${formId}`, payload())
      : await axios.post(`/teams/${team.id}/signup_forms`, payload());
    setFormId(res.data.id);
    return res;
  };

  const applyServerState = (data) => {
    setSavedUuid(data.uuid);
    setPublicPath(data.public_path);
    setStatus(data.status);
  };

  const errorFrom = (e, fallback) => {
    const body = e.response?.data;
    return Array.isArray(body) ? body[0] : fallback;
  };

  const save = async () => {
    if (!name.trim()) { toastError("Give your form a name first."); return; }
    setSaving(true);
    try {
      const res = await persist();
      toastSuccess("Signup form saved.");
      if (!isPersisted) { window.location.href = `/teams/${team.id}/signup_forms/${res.data.id}/edit`; }
      else { applyServerState(res.data); }
    } catch (e) {
      toastError(errorFrom(e, "Could not save the form."));
    } finally { setSaving(false); }
  };

  const getEmbed = async () => {
    if (!hasVenue) return;
    if (!name.trim()) { toastError("Give your form a name first."); return; }
    setSaving(true);
    try {
      const res = await persist();
      if (!isPersisted) { window.location.href = `/teams/${team.id}/signup_forms/${res.data.id}/edit?embed=1`; return; }
      applyServerState(res.data);
      setModalOpen(true);
    } catch (e) {
      toastError(errorFrom(e, "Could not generate the embed code."));
    } finally { setSaving(false); }
  };

  const publish = async () => {
    if (!hasVenue) return;
    if (!name.trim()) { toastError("Give your form a name first."); return; }
    setSaving(true);
    try {
      const saved = await persist();
      const res = await axios.post(`/teams/${team.id}/signup_forms/${saved.data.id}/publish`);
      if (!isPersisted) { window.location.href = `/teams/${team.id}/signup_forms/${res.data.id}/edit?embed=1`; return; }
      applyServerState(res.data);
      toastSuccess("Form published — it's now collecting signups.");
      setModalOpen(true);
    } catch (e) {
      toastError(errorFrom(e, "Could not publish the form."));
    } finally { setSaving(false); }
  };

  const unpublish = async () => {
    if (!window.confirm("Unpublish this form? Its embed will stop collecting signups until you publish it again.")) return;
    setSaving(true);

    try {
      withCsrf();
      applyServerState((await axios.post(`/teams/${team.id}/signup_forms/${formId}/unpublish`)).data);
    } catch (e) {
      toastError(errorFrom(e, "Could not unpublish the form."));
      setSaving(false);
      return;
    }

    try {
      applyServerState((await persist()).data);
      toastSuccess("Form unpublished — its embed is no longer collecting signups.");
    } catch (e) {
      toastError(`Form unpublished, but your latest edits could not be saved. ${errorFrom(e, "")}`.trim());
    } finally { setSaving(false); }
  };

  useEffect(() => {
    if (isPersisted && new URLSearchParams(window.location.search).has("embed")) setModalOpen(true);
  }, [isPersisted]);

  const embedCode = useMemo(() => buildSignupFormEmbed(formForEmbed, { embedBaseUrl }), [formForEmbed, embedBaseUrl]);

  // The heading is an uncontrolled contentEditable: we seed its text once here and
  // never render `name` back as a child. Rendering the state as children makes React
  // rewrite the text node on every keystroke, which collapses the caret to offset 0
  // and types the name in backwards ("Form" -> "mroF"). Same pattern as ConsentItem.
  // Layout effect so the seeded name is there on first paint — a plain effect runs
  // after paint and flashes the "Untitled form" placeholder for a frame.
  useLayoutEffect(() => {
    if (nameRef.current) nameRef.current.textContent = name;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onNameInput = (e) => setName(e.currentTarget.textContent);

  return (
    <div className="sf-builder">
      <style>{signupFormBuilderStyles}</style>
      <ToastContainer icon={false} />

      <div className="sf-pagehead">
        <PageHeader
          breadcrumbs={(
            <Breadcrumbs items={[
              { label: "Signup Forms", href: listHref(`/teams/${team.id}/signup_forms`) },
              { label: name || "Untitled form" },
            ]} />
          )}
          title={(
            <span className="sf-title-wrap">
              <span className="sf-title" contentEditable suppressContentEditableWarning spellCheck={false}
                role="textbox" aria-label="Form name" data-placeholder="Untitled form" ref={nameRef} onInput={onNameInput}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }} />
              <Button variant="tertiary" size="xs" icon={Edit05} aria-label="Rename form" title="Rename form"
                onClick={() => { nameRef.current?.focus(); document.execCommand("selectAll", false, null); }} />
              <FormStatusBadge status={status} />
            </span>
          )}
          supportingText={isLive ? (
            <>
              This form is live — its embed is collecting signups. Saving an edit keeps it live, and your site shows it right away.
              <span className="sf-recopy-hint">Pasted a long block of embed code before? Replace it with the new, shorter code so your site picks up your edits.</span>
            </>
          ) : "This form is a draft. Until you publish it, the form on your site says it isn't accepting signups."}
          actions={(
            <>
              {formId && (
                <Button as="a" href={`/teams/${team.id}/signup_forms?submissions=${formId}`} variant="link-color">View submissions</Button>
              )}
              <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
              {isLive && <Button onClick={unpublish} disabled={saving}>Unpublish</Button>}
              <Button variant={isLive ? "primary" : "secondary"} icon={Code02} onClick={getEmbed}
                disabled={!hasVenue || saving} title={hasVenue ? "" : "Select a venue first"}>
                Get embed code
              </Button>
              {!isLive && (
                <Button variant="primary" onClick={publish} disabled={!hasVenue || saving} title={hasVenue ? "" : "Select a venue first"}>
                  Publish
                </Button>
              )}
            </>
          )}
        />
      </div>

      <div className="sf-builder-content">
        <div className="sf-builder-main">

          <BuilderSection title="Venue" desc="Which venue(s) this form collects subscribers for. The venue ID is embedded into the snippet automatically.">
            <Select
              label="Collecting for"
              multiple
              searchable
              placeholder="Select venue(s)"
              options={venueOwnerships.map((vo) => ({ value: vo.id, label: vo.name }))}
              value={venueIds.filter((id) => !hiddenVenueIds.includes(id))}
              onChange={(ids) => setVenueIds([...ids, ...hiddenVenueIds])}
              hint={hiddenVenueIds.length > 0
                ? `Also collecting for ${hiddenVenueIds.length} ${hiddenVenueIds.length === 1 ? "venue" : "venues"} you don't have access to.`
                : undefined}
            />
          </BuilderSection>

          <BuilderSection title="Form fields" desc="Choose what to collect and customize the labels. Email is always required.">
            {FIELD_ORDER.map((key) => {
              const f = fields[key] || {};
              const isEmail = key === "email";
              const lockedOn = isEmail || (key === "phone" && channels.sms);
              const on = lockedOn ? true : f.enabled !== false;
              return (
                <div key={key} className="sf-field-toggle">
                  <div className="sf-field-toggle-head">
                    <Toggle
                      id={`sf-field-${key}`}
                      size="sm"
                      checked={on}
                      disabled={lockedOn}
                      onChange={() => setField(key, { enabled: !on })}
                    />
                    <label className="sf-field-toggle-title" htmlFor={`sf-field-${key}`}>
                      {FIELD_TITLES[key]}
                      {lockedOn && <Badge color="brand" size="sm">Required</Badge>}
                    </label>
                  </div>
                  {on && (
                    <div className="sf-field-toggle-detail">
                      <TextInput size="sm" label="Label" value={f.label || ""} onChange={(e) => setField(key, { label: e.target.value })} />
                      <TextInput size="sm" label="Placeholder" value={f.placeholder || ""} onChange={(e) => setField(key, { placeholder: e.target.value })} />
                      <div className="sf-span-all">
                        <Checkbox
                          size="sm"
                          checked={isEmail ? true : !!f.required}
                          disabled={isEmail}
                          onChange={(e) => setField(key, { required: e.target.checked })}
                          label={isEmail ? "Required field (email is always required)" : "Required field"}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </BuilderSection>

          <BuilderSection title="Custom questions" desc="Ask anything else. Answers show up in this form's submissions.">
            <CustomQuestionsEditor questions={questions} onChange={setQuestions} isLive={isLive} />
          </BuilderSection>

          <BuilderSection title="Subscription preferences" desc="How subscribers opt in to your marketing. Pair a channel with an agreement checkbox and only fans who tick it join that list.">
            {CHANNEL_SECTIONS.map(({ key, title, desc }) => {
              const agreements = agreementsFor(key);

              return (
                <div className="sf-field-toggle" key={key}>
                  <Toggle size="sm" label={title} description={desc} checked={channels[key]} onChange={() => toggleChannel(key)} />
                  {agreements.length > 0 && (
                    <div className="sf-agreements">
                      <p className="sf-hint">
                        {channels[key] ? AGREEMENT_HINT[key] : `This form no longer subscribes fans to ${title.toLowerCase()}, so this checkbox does nothing. Remove it or switch the channel back on.`}
                      </p>
                      {agreements.map((agreement) => (
                        <ConsentItem key={agreement.cid} consent={agreement} editorRefs={editorRefs}
                          onChange={(html) => updateConsent(agreement.cid, { text: html })}
                          onBold={() => { document.execCommand("bold"); updateConsent(agreement.cid, { text: editorRefs.current[agreement.cid].innerHTML }); }}
                          onChk={key === "sms" ? null : (v) => updateConsent(agreement.cid, { checked: v })}
                          onRemove={() => removeConsent(agreement.cid)} />
                      ))}
                    </div>
                  )}
                  {channels[key] && agreements.length === 0 && (
                    <div className="sf-agreements">
                      <Button variant="link-color" size="sm" icon={Plus} onClick={() => addAgreement(key)}>Add an agreement checkbox</Button>
                    </div>
                  )}
                </div>
              );
            })}
            <div className="sf-field-toggle">
              <Checkbox
                size="sm"
                checked={letChoose}
                onChange={(e) => setLetChoose(e.target.checked)}
                label="Let subscribers choose their channel"
                aria-describedby="sf-let-choose-hint"
              />
              <p id="sf-let-choose-hint" className="sf-hint sf-indent">Show email/SMS checkboxes so fans pick how they hear from you.</p>
              {letChoose && (
                <p className="sf-hint sf-indent">Only channels without an agreement checkbox get one of these — where you paired an agreement, that box is the opt-in. Email starts ticked and SMS starts unticked; that pair is fixed, because a pre-ticked SMS opt-in fails carrier review.</p>
              )}
            </div>

            <div className="sf-field sf-other-consents">
              <span className="sf-label">Other checkboxes</span>
              <p className="sf-hint">
                Anything else fans agree to before subscribing — terms acceptance, an age confirmation. Use <strong>B</strong> and the link button to format text and link to your policies. These don't affect which lists a fan joins; the agreement paired with each channel above does that.
              </p>
              {standaloneConsents.map((c) => (
                <ConsentItem key={c.cid} consent={c} editorRefs={editorRefs}
                  onChange={(html) => updateConsent(c.cid, { text: html })}
                  onBold={() => { document.execCommand("bold"); updateConsent(c.cid, { text: editorRefs.current[c.cid].innerHTML }); }}
                  onReq={(v) => updateConsent(c.cid, { required: v })}
                  onChk={(v) => updateConsent(c.cid, { checked: v })}
                  onRemove={() => removeConsent(c.cid)} />
              ))}
              <div>
                <Button variant="link-color" size="sm" icon={Plus} onClick={addConsent}>Add checkbox</Button>
              </div>
            </div>
          </BuilderSection>

          <BuilderSection title="Tags" desc="Applied to every fan who subscribes through this form. New tags are created automatically.">
            <TextInput
              label="Tags"
              placeholder="Type a tag and press Enter"
              hint={<>e.g. <em>website</em>, <em>newsletter</em>, a campaign name.</>}
              value={tagDraft}
              onChange={(e) => setTagDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && tagDraft.trim()) { e.preventDefault(); addTag(tagDraft); }
                if (e.key === "Backspace" && !tagDraft && tags.length) setTags((ts) => ts.slice(0, -1));
              }}
            />
            {tags.length > 0 && (
              <div className="sf-tags">
                {tags.map((t) => (
                  <Tag key={t} size="sm" onDismiss={() => setTags((ts) => ts.filter((x) => x !== t))}>{t}</Tag>
                ))}
              </div>
            )}
          </BuilderSection>

          <BuilderSection title="Appearance" desc="Match the form to your website. Colors and corners apply to the embedded form.">
            <div className="sf-field-row">
              <TextInput label="Heading (optional)" value={text.heading || ""} placeholder="e.g. Join our mailing list" onChange={(e) => { const { value } = e.target; setText((t) => ({ ...t, heading: value })); }} />
              <TextInput label="Button label" value={text.button_label || ""} onChange={(e) => { const { value } = e.target; setText((t) => ({ ...t, button_label: value })); }} />
            </div>
            <TextInput label="Subheading (optional)" value={text.subheading || ""} placeholder="A short line under the heading" onChange={(e) => { const { value } = e.target; setText((t) => ({ ...t, subheading: value })); }} />
            <div className="sf-field">
              <span className="sf-label">Colors</span>
              <div className="sf-color-grid">
                <ColorField label="Background" value={appearance.background_color} onChange={(v) => setAppr({ background_color: v })} />
                <ColorField label="Button" value={appearance.button_color} onChange={(v) => setAppr({ button_color: v })} />
                <ColorField label="Button text" value={appearance.button_text_color} onChange={(v) => setAppr({ button_text_color: v })} />
                <ColorField label="Text" value={appearance.text_color} onChange={(v) => setAppr({ text_color: v })} />
                <ColorField label="Input border" value={appearance.input_border_color} onChange={(v) => setAppr({ input_border_color: v })} />
              </div>
            </div>
            <div className="sf-field">
              <label className="sf-label" htmlFor="sf-corner-radius">Corner radius <span className="sf-label-note">(0 = square)</span></label>
              <div className="sf-range-row">
                <input id="sf-corner-radius" type="range" className="sf-range" min="0" max="30" value={appearance.corner_radius ?? 0} onChange={(e) => setAppr({ corner_radius: Number(e.target.value) })} />
                <span className="sf-range-val">{appearance.corner_radius ?? 0}px</span>
              </div>
            </div>
            <Toggle size="sm" label="Drop shadow" description="Off blends the form flat into your site. On lifts it like a card."
              checked={!!appearance.drop_shadow} onChange={() => setAppr({ drop_shadow: !appearance.drop_shadow })} />
          </BuilderSection>

          <BuilderSection title="After submit" desc="What a fan sees once they subscribe.">
            <RadioGroup value={success.mode} onChange={(mode) => setSuccess((s) => ({ ...s, mode }))}>
              <div className="sf-radio-list">
                <Radio value="message" label="Show a thank-you message" />
                {success.mode === "message" && (
                  <div className="sf-indent">
                    <TextInput aria-label="Thank-you message" value={success.message} onChange={(e) => { const { value } = e.target; setSuccess((s) => ({ ...s, message: value })); }} />
                  </div>
                )}
                <Radio value="redirect" label="Redirect to a URL" />
                {success.mode === "redirect" && (
                  <div className="sf-indent">
                    <TextInput aria-label="Redirect URL" value={success.redirect} placeholder="https://yourvenue.com/thank-you" onChange={(e) => { const { value } = e.target; setSuccess((s) => ({ ...s, redirect: value })); }} />
                  </div>
                )}
              </div>
            </RadioGroup>
          </BuilderSection>

          <BuilderSection title="Email alerts" desc="Email these team members each time someone submits this form. Leave it empty to send no emails.">
            <EmailAlertsEditor teamMembers={teamMembers} value={alertIds} onChange={setAlertIds} />
          </BuilderSection>
        </div>

        <div className="sf-builder-sidebar">
          <div className="sf-preview-head">
            <span className="sf-preview-title"><Eye width={16} height={16} /> Live preview</span>
          </div>
          <div className="sf-preview-stage">
            <FormPreview fields={fields} channels={channels} letChoose={letChoose} consents={consents} questions={questions} appearance={appearance} text={text} success={success} />
          </div>
        </div>
      </div>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        size="lg"
        icon={Code02}
        title="Embed your form"
        description="Paste this snippet into your website where you want the form to appear. It works on any site — Squarespace, Wix, WordPress, or custom HTML. Edits you save show up on your site automatically."
        footer={(
          <>
            <Button onClick={() => setModalOpen(false)}>Close</Button>
            {!isLive && (
              <Button onClick={publish} disabled={!hasVenue || saving}>Publish now</Button>
            )}
            <CopyButton variant="primary" label="Copy code" copiedLabel="Copied" icon={Copy01} value={embedCode} />
          </>
        )}
      >
        {!isLive && (
          <div className="sf-modal-warning">
            <Alert color="warning" title="This form is still a draft." description="Until you publish it, the form on your site says it isn't accepting signups." />
          </div>
        )}
        <pre className="sf-code-block">{embedCode}</pre>
        {publicPath && (
          <p className="sf-direct-link">
            Prefer a link? Share the form directly:{" "}
            <a href={`${embedBaseUrl}${publicPath}`} target="_blank" rel="noopener noreferrer">{`${embedBaseUrl}${publicPath}`}</a>
          </p>
        )}
      </Modal>
    </div>
  );
};

export default SignupFormBuilderContainer;
